import { differenceInMinutes, isFuture, isPast } from 'date-fns';
import * as Linking from 'expo-linking';
import { VideoIcon } from 'lucide-react-native';
import type { GestureResponderEvent } from 'react-native';
import { View, XStack, YStack } from 'tamagui';

import { Button, ButtonSize, ButtonVariant } from '../buttons/Button';
import { FrostedSurface } from '../FrostedSurface';
import { Shadows } from '../Shadows';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    H1Emphasized,
    H5Emphasized,
} from '../text/Typography';

type Variant = 'card' | 'row' | 'frosted';

export interface UpcomingSessionWidgetProps {
    title: string;
    session: {
        startTime: string;
        endTime: string;
        videoConferenceUrl: string;
        attendeeTimezone?: string | null;
    };
    guide?: {
        firstName?: string | null;
    } | null;
    onCardPress?: () => void;
    maxWidth?: number;
    variant?: Variant;
}

const isTodayInTimezone = (date: Date, timezone?: string): boolean => {
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    });

    const nowFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    });

    const sessionDateStr = dateFormatter.format(date);
    const todayDateStr = nowFormatter.format(new Date());

    return sessionDateStr === todayDateStr;
};

const isTomorrowInTimezone = (date: Date, timezone?: string): boolean => {
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    });

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const tomorrowFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    });

    const sessionDateStr = dateFormatter.format(date);
    const tomorrowDateStr = tomorrowFormatter.format(tomorrow);

    return sessionDateStr === tomorrowDateStr;
};

/**
 * Check if session starts within N minutes
 */
const sessionStartTimeIsWithinNMinutes = (
    startTime: string,
    minutes: number,
): boolean => {
    const diff = differenceInMinutes(new Date(startTime), new Date());
    return diff >= 0 && diff < minutes;
};

/**
 * Check if session is currently in progress
 */
const sessionIsInProgress = (startTime: string, endTime: string): boolean => {
    const isAfterStart = isPast(new Date(startTime));
    const isBeforeEnd = isFuture(new Date(endTime));
    return isAfterStart && isBeforeEnd;
};

export const UpcomingSessionWidget = ({
    title,
    session,
    guide,
    onCardPress,
    maxWidth,
    variant = 'card',
}: UpcomingSessionWidgetProps) => {
    const { startTime, endTime, videoConferenceUrl, attendeeTimezone } =
        session;
    const sessionDate = new Date(startTime);
    const timezone = attendeeTimezone ?? undefined;
    const sessionIsToday = isTodayInTimezone(sessionDate, timezone);
    const sessionIsTomorrow = isTomorrowInTimezone(sessionDate, timezone);

    // Compute joinability: show join button if within 5 minutes OR in progress
    const sessionStartsSoon = sessionStartTimeIsWithinNMinutes(startTime, 5);
    const sessionInProgress = sessionIsInProgress(startTime, endTime);
    const isJoinable = sessionStartsSoon || sessionInProgress;

    // Format guide name with fallback
    const guideName = guide?.firstName ?? 'your Guide';

    // Format date for the date box (e.g., "Jan 13")
    const monthFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        month: 'short',
    });
    const month = monthFormatter.format(sessionDate);

    const dayFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        day: '2-digit',
    });
    const day = dayFormatter.format(sessionDate);

    // Format subtitle (e.g., "Today • 10:30 AM", "Tomorrow • 11:30 AM", or "Tue, Jan 13 • 11:30 AM")
    let dayText: string;
    if (sessionIsToday) {
        dayText = 'Today';
    } else if (sessionIsTomorrow) {
        dayText = 'Tomorrow';
    } else {
        const dateFormatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            weekday: 'short',
            month: 'short',
            day: 'numeric',
        });
        dayText = dateFormatter.format(sessionDate);
    }

    const timeFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
    });
    const time = timeFormatter.format(sessionDate);

    const subtitle = `${dayText}  •  ${time}`;
    const guideText =
        variant === 'frosted'
            ? `Video call with ${guideName}`
            : `Video session with ${guideName}`;

    // Handle join button press - open video conference URL
    const handleJoinPress = (e?: GestureResponderEvent) => {
        // Stop event from bubbling to parent card's onPress
        e?.stopPropagation();
        void Linking.openURL(videoConferenceUrl);
    };

    const cardContent = (
        <>
            <XStack gap={16} alignItems="center">
                <YStack
                    borderRadius={16}
                    paddingHorizontal={16}
                    paddingTop={6}
                    paddingBottom={6}
                    alignItems="center"
                    justifyContent="center"
                    minWidth={80}
                    backgroundColor={
                        variant === 'row' ? '$peach400' : '$peach300'
                    }
                >
                    <BodyMediumRegular color="$gray1200">
                        {month}
                    </BodyMediumRegular>
                    <H1Emphasized color="$gray1200">{day}</H1Emphasized>
                </YStack>
                <YStack gap={4} justifyContent="space-between">
                    {variant === 'row' ? (
                        <BodyLargeEmphasized color="$gray1200">
                            {title}
                        </BodyLargeEmphasized>
                    ) : (
                        <H5Emphasized color="$gray1200">{title}</H5Emphasized>
                    )}
                    <View>
                        <BodyMediumRegular
                            color={
                                variant === 'row' ? '$gray1100' : '$gray1000'
                            }
                        >
                            {subtitle}
                        </BodyMediumRegular>
                        <BodyMediumRegular
                            color={
                                variant === 'row' ? '$gray1100' : '$gray1000'
                            }
                        >
                            {guideText}
                        </BodyMediumRegular>
                    </View>
                </YStack>
            </XStack>
            {isJoinable && (
                <View
                    onPress={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        tone="positive"
                        size={ButtonSize.LARGE}
                        text="Join the session"
                        icon={VideoIcon}
                        onPress={handleJoinPress}
                        width="full"
                    />
                </View>
            )}
        </>
    );

    if (variant === 'frosted') {
        return (
            <FrostedSurface width="100%" maxWidth={maxWidth} alignSelf="center">
                <YStack
                    padding={16}
                    gap={16}
                    cursor={onCardPress ? 'pointer' : undefined}
                    onPress={onCardPress}
                >
                    {cardContent}
                </YStack>
            </FrostedSurface>
        );
    }

    return (
        <YStack
            padding={16}
            backgroundColor={variant === 'row' ? '$gray200' : '$white'}
            borderRadius={24}
            gap={16}
            width="100%"
            maxWidth={maxWidth}
            alignSelf="center"
            {...(variant === 'card' ? Shadows.MEDIUM : {})}
            cursor={onCardPress ? 'pointer' : undefined}
            onPress={onCardPress}
        >
            {cardContent}
        </YStack>
    );
};
